import { useState } from "react";
import api from "../services/api";
import { useSnackbarAlert } from "../hooks/useSnackbarAlert";

const defaultEmployee = {
  firstName: "",
  lastName: "",
  employeeID: "",
  posPasscode: "",
  role: "staff",
  workLocation: "", // ObjectId of StoreLocation
  photo: "",
  isActive: true,
  phone: "",
  email: "",
  shiftPreferences: [],
  permissions: {
    canUpdateOrders: true,
    canIssueRefunds: false,
    canAdjustPoints: true,
    canEditProducts: false,
  },
  notes: "",
};

const useEmployeeFormData = () => {
  const [formData, setFormData] = useState(defaultEmployee);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const snackbar = useSnackbarAlert();

  const resetForm = () => {
    setFormData(defaultEmployee);
    setErrors({});
    setIsEditMode(false);
    setCurrentId(null);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const handlePermissionChange = (permissionKey, value) => {
    setFormData((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permissionKey]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setErrors({});

    try {
      if (isEditMode) {
        const res = await api.put(`/admin/employees/${currentId}`, formData);
        snackbar.success("Employee profile updated");
        return { success: true, data: res.data };
      } else {
        const res = await api.post("/admin/employees", formData);
        snackbar.success("Employee profile created");
        return { success: true, data: res.data };
      }
    } catch (err) {
      console.error("Failed to submit employee:", err);
      snackbar.error("Employee profile failed to create because of ", err);
      setErrors(err.response?.data?.errors || {});
      return { success: false };
    } finally {
      setIsSubmitting(false);
    }
  };

  const populateForm = (employee) => {
    setFormData({
      firstName: employee.firstName || "",
      lastName: employee.lastName || "",
      employeeID: employee.employeeID || "",
      posPasscode: "", // never prefill passwords
      role: employee.role || "staff",
      workLocation: employee.workLocation || "",
      photo: employee.photo || "",
      isActive: employee.isActive ?? true,
      phone: employee.phone || "",
      email: employee.email || "",
      shiftPreferences: employee.shiftPreferences || [],
      permissions: {
        canUpdateOrders: employee.permissions?.canUpdateOrders ?? true,
        canIssueRefunds: employee.permissions?.canIssueRefunds ?? false,
        canAdjustPoints: employee.permissions?.canAdjustPoints ?? true,
        canEditProducts: employee.permissions?.canEditProducts ?? false,
      },
      notes: employee.notes || "",
    });
    setIsEditMode(true);
    setCurrentId(employee._id);
  };

  return {
    formData,
    setFormData,
    handleChange,
    handlePermissionChange,
    handleSubmit,
    resetForm,
    populateForm,
    isSubmitting,
    isEditMode,
    errors,
  };
};

export default useEmployeeFormData;
